/**
 * WebSocket URL Helper
 * Automatically uses WS or WSS based on page protocol
 * Works with localhost, ngrok, and production domains
 */

/**
 * Get the WebSocket URL for a given endpoint
 * Automatically detects HTTP vs HTTPS and uses WS vs WSS accordingly
 *
 * @param endpoint - The WebSocket endpoint path (e.g., '/ws/forms/123' or '/asr?session_id=abc')
 * @param apiBaseUrl - Optional custom API base URL (defaults to VITE_API_URL or current origin)
 * @returns Complete WebSocket URL (ws:// or wss://)
 */
export function getWebSocketUrl(endpoint: string, apiBaseUrl?: string): string {
  // Ensure endpoint starts with /
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

  // Get base URL
  let baseUrl = apiBaseUrl || import.meta.env.VITE_API_URL;

  console.log('[getWebSocketUrl] 📥 API Base URL:', baseUrl);

  // If no API URL specified, use current page origin
  if (!baseUrl) {
    baseUrl = window.location.origin;
  }

  // Parse the base URL to get protocol and host
  let wsProtocol: string;
  let host: string;

  try {
    const url = new URL(baseUrl);

    // Determine WebSocket protocol based on page protocol
    if (url.protocol === 'https:') {
      wsProtocol = 'wss:';
    } else {
      wsProtocol = 'ws:';
    }

    host = url.host; // Includes hostname and port
  } catch (error) {
    // Fallback: If baseUrl is not a complete URL, construct from current page
    console.warn('[WebSocket] Invalid base URL, using window.location', error);

    // Use current page protocol to determine WS protocol
    if (window.location.protocol === 'https:') {
      wsProtocol = 'wss:';
    } else {
      wsProtocol = 'ws:';
    }

    // Try to parse baseUrl as host:port
    if (baseUrl.includes('://')) {
      const parts = baseUrl.split('://')[1];
      host = parts;
    } else {
      host = baseUrl;
    }
  }

  // Construct final WebSocket URL
  const wsUrl = `${wsProtocol}//${host}${cleanEndpoint}`;

  console.log('[WebSocket] Generated URL:', {
    endpoint: cleanEndpoint,
    baseUrl,
    protocol: wsProtocol,
    host,
    finalUrl: wsUrl
  });

  return wsUrl;
}

/**
 * Get API base URL with correct protocol
 * @returns HTTP or HTTPS URL for API calls
 */
export function getApiBaseUrl(): string {
  return import.meta.env.VITE_API_URL || window.location.origin;
}

/**
 * Check if current page is secure (HTTPS)
 */
export function isSecureContext(): boolean {
  return window.location.protocol === 'https:';
}

/**
 * Fetch wrapper that adds ngrok-skip-browser-warning header
 * Use this for all API calls to avoid ngrok interstitial page
 *
 * @param url - The URL to fetch (relative or absolute)
 * @param options - Standard fetch options
 * @returns Promise with Response
 */
export async function fetchAPI(url: string, options?: RequestInit): Promise<Response> {
  // Add ngrok header to existing headers
  const headers = new Headers(options?.headers || {});
  headers.set('ngrok-skip-browser-warning', 'true');

  return fetch(url, {
    ...options,
    headers,
  });
}
